// Host Server UI — form for creating a new server

import { createServer, getMap, type CreateServerResult } from "../../engine/launcher.js";
import type { ServerConfig } from "../../net/server.js";
import { PRESETS, PRESET_KEYS, DEFAULT_PRESET } from "../serverconfig.js";
import { FONT, PANEL_BG, BORDER, TEXT, TEXT_DIM, ACCENT, INPUT_BG } from "./theme.js";

const BUILTIN_MAPS = ["surf", "ocean", "cornell", "bhop_test", "stress"];

export type HostCallbacks = {
  onCreated: (result: CreateServerResult) => void;
  onBack: () => void;
};

export class HostServerUI {
  private el: HTMLDivElement;
  private customMapJson: string | null = null;
  private sliders = new Map<string, HTMLInputElement>();
  private errorEl!: HTMLDivElement;
  private mapCodeInput!: HTMLInputElement;
  private mapCodeStatus!: HTMLDivElement;

  constructor(private relayHttp: string, private callbacks: HostCallbacks) {
    this.el = document.createElement("div");
    this.el.style.cssText =
      `position:fixed;inset:0;display:flex;align-items:center;justify-content:center;` +
      `z-index:1100;background:rgba(0,0,0,0.6);backdrop-filter:blur(4px);`;

    // Click outside to go back
    this.el.addEventListener("click", (e) => {
      if (e.target === this.el) callbacks.onBack();
    });

    const panel = document.createElement("div");
    panel.style.cssText =
      `width:380px;max-height:90vh;overflow-y:auto;background:${PANEL_BG};border:1px solid ${BORDER};padding:24px;`;

    const title = document.createElement("div");
    title.textContent = "HOST SERVER";
    title.style.cssText = `font:bold 16px ${FONT};color:${TEXT};letter-spacing:2px;margin-bottom:20px;`;
    panel.appendChild(title);

    // Server name
    const nameInput = this.addField(panel, "Server Name");
    nameInput.value = "My Server";
    nameInput.maxLength = 80;

    // Map selection
    this.addLabel(panel, "Map");
    const mapSelect = document.createElement("select");
    mapSelect.style.cssText = inputStyle();
    for (const m of BUILTIN_MAPS) {
      const opt = document.createElement("option");
      opt.value = m;
      opt.textContent = m;
      mapSelect.appendChild(opt);
    }
    const customOpt = document.createElement("option");
    customOpt.value = "__custom";
    customOpt.textContent = "Upload custom map...";
    mapSelect.appendChild(customOpt);
    panel.appendChild(mapSelect);

    // Hidden file input for custom maps
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = ".json";
    fileInput.style.display = "none";
    panel.appendChild(fileInput);

    const customLabel = document.createElement("div");
    customLabel.style.cssText = `font:10px ${FONT};color:${ACCENT};margin-top:4px;margin-bottom:2px;min-height:14px;`;
    panel.appendChild(customLabel);

    mapSelect.addEventListener("change", () => {
      if (mapSelect.value === "__custom") {
        fileInput.click();
      } else {
        this.customMapJson = null;
        customLabel.textContent = "";
      }
    });

    fileInput.addEventListener("change", () => {
      const file = fileInput.files?.[0];
      if (!file) { mapSelect.value = BUILTIN_MAPS[0]; return; }
      const reader = new FileReader();
      reader.onload = () => {
        this.customMapJson = reader.result as string;
        customLabel.textContent = `Loaded: ${file.name}`;
      };
      reader.readAsText(file);
    });

    // Community map code
    this.mapCodeInput = this.addField(panel, "Community Map Code");
    this.mapCodeInput.placeholder = "e.g. SURF-7K3M";
    this.mapCodeInput.maxLength = 20;
    this.mapCodeStatus = document.createElement("div");
    this.mapCodeStatus.style.cssText = `font:10px ${FONT};color:${TEXT_DIM};margin-top:2px;margin-bottom:2px;min-height:14px;`;
    panel.appendChild(this.mapCodeStatus);

    // When a code is entered, validate and show map name
    this.mapCodeInput.addEventListener("input", async () => {
      const code = this.mapCodeInput.value.trim();
      if (!code) { this.mapCodeStatus.textContent = ""; return; }
      try {
        const map = await getMap(this.relayHttp, code);
        this.mapCodeStatus.textContent = `\u2713 ${map.name}`;
        this.mapCodeStatus.style.color = "#5cb47a";
      } catch {
        this.mapCodeStatus.textContent = "Map not found";
        this.mapCodeStatus.style.color = TEXT_DIM;
      }
    });

    // Password (optional)
    const passwordInput = this.addField(panel, "Password (optional)");
    passwordInput.type = "password";
    passwordInput.placeholder = "Leave empty for public";
    passwordInput.maxLength = 32;

    // Max players
    const maxPlayersInput = this.addField(panel, "Max Players");
    maxPlayersInput.type = "number";
    maxPlayersInput.value = "8";
    maxPlayersInput.min = "1";
    maxPlayersInput.max = "16";

    // --- Preset selector ---
    this.addLabel(panel, "Settings");
    const presetSelect = document.createElement("select");
    presetSelect.style.cssText = inputStyle();
    for (const key of PRESET_KEYS) {
      const opt = document.createElement("option");
      opt.value = key;
      opt.textContent = PRESETS[key].name;
      presetSelect.appendChild(opt);
    }
    const customPresetOpt = document.createElement("option");
    customPresetOpt.value = "__custom";
    customPresetOpt.textContent = "Custom...";
    presetSelect.appendChild(customPresetOpt);
    presetSelect.value = DEFAULT_PRESET;
    panel.appendChild(presetSelect);

    // Preset description
    const presetDesc = document.createElement("div");
    presetDesc.style.cssText = `font:10px ${FONT};color:${TEXT_DIM};margin-top:4px;margin-bottom:2px;min-height:14px;`;
    presetDesc.textContent = PRESETS[DEFAULT_PRESET].description;
    panel.appendChild(presetDesc);

    // --- Custom sliders (hidden by default) ---
    const customSection = document.createElement("div");
    customSection.style.display = "none";
    panel.appendChild(customSection);

    const warning = document.createElement("div");
    warning.style.cssText = `font:10px ${FONT};color:#c49555;margin:8px 0 6px;`;
    warning.textContent = "Custom settings are untested — players will see this server labeled as Custom.";
    customSection.appendChild(warning);

    const classicPreset = PRESETS.classic;
    const sliderDefs: { key: string; label: string; min: number; max: number; step: number; value: number }[] = [
      { key: "maxSpeed",     label: "Max Speed",     min: 1,  max: 30,  step: 1,   value: classicPreset.controller.maxSpeed },
      { key: "airAccel",     label: "Air Accel",     min: 1,  max: 300, step: 5,   value: classicPreset.controller.airAccel },
      { key: "groundAccel",  label: "Ground Accel",  min: 1,  max: 30,  step: 1,   value: classicPreset.controller.groundAccel },
      { key: "friction",     label: "Friction",       min: 1,  max: 20,  step: 0.5, value: classicPreset.controller.friction },
      { key: "jumpVelocity", label: "Jump Velocity",  min: 1,  max: 20,  step: 0.5, value: classicPreset.controller.jumpVelocity },
      { key: "gravity",      label: "Gravity",        min: 5,  max: 50,  step: 1,   value: classicPreset.gravity },
    ];

    for (const def of sliderDefs) {
      this.addSlider(customSection, def.key, def.label, def.min, def.max, def.step, def.value);
    }

    // Preset change handler
    presetSelect.addEventListener("change", () => {
      const key = presetSelect.value;
      if (key === "__custom") {
        customSection.style.display = "block";
        presetDesc.textContent = "";
      } else {
        customSection.style.display = "none";
        presetDesc.textContent = PRESETS[key].description;
        // Reset sliders to this preset's values
        const p = PRESETS[key];
        this.setSlider("maxSpeed", p.controller.maxSpeed);
        this.setSlider("airAccel", p.controller.airAccel);
        this.setSlider("groundAccel", p.controller.groundAccel);
        this.setSlider("friction", p.controller.friction);
        this.setSlider("jumpVelocity", p.controller.jumpVelocity);
        this.setSlider("gravity", p.gravity);
      }
    });

    // Spacer
    const spacer = document.createElement("div");
    spacer.style.height = "20px";
    panel.appendChild(spacer);

    // Buttons
    const btnRow = document.createElement("div");
    btnRow.style.cssText = "display:flex;gap:8px;";

    const createBtn = document.createElement("button");
    createBtn.textContent = "HOST";
    createBtn.style.cssText =
      `flex:1;padding:8px;background:${ACCENT};color:#fff;border:none;cursor:pointer;` +
      `font:bold 12px ${FONT};letter-spacing:1px;`;
    createBtn.addEventListener("click", () => this.submit(nameInput, passwordInput, mapSelect, maxPlayersInput, presetSelect, createBtn));

    const backBtn = document.createElement("button");
    backBtn.textContent = "BACK";
    backBtn.style.cssText =
      `padding:8px 16px;background:transparent;color:${TEXT_DIM};border:1px solid ${BORDER};` +
      `cursor:pointer;font:11px ${FONT};`;
    backBtn.addEventListener("click", () => callbacks.onBack());

    btnRow.append(createBtn, backBtn);
    panel.appendChild(btnRow);

    // Error display
    this.errorEl = document.createElement("div");
    this.errorEl.style.cssText = `font:11px ${FONT};color:#e55;margin-top:10px;min-height:16px;`;
    panel.appendChild(this.errorEl);

    this.el.appendChild(panel);
  }

  mount(mapCode?: string): void {
    document.body.appendChild(this.el);
    if (mapCode) {
      this.mapCodeInput.value = mapCode;
      this.mapCodeInput.dispatchEvent(new Event("input"));
    }
  }

  unmount(): void {
    this.el.remove();
  }

  private addLabel(parent: HTMLElement, text: string): void {
    const label = document.createElement("div");
    label.textContent = text;
    label.style.cssText = `font:10px ${FONT};color:${TEXT_DIM};letter-spacing:1px;text-transform:uppercase;margin-bottom:4px;margin-top:12px;`;
    parent.appendChild(label);
  }

  private addField(parent: HTMLElement, label: string): HTMLInputElement {
    this.addLabel(parent, label);
    const input = document.createElement("input");
    input.style.cssText = inputStyle();
    parent.appendChild(input);
    return input;
  }

  private addSlider(parent: HTMLElement, key: string, label: string, min: number, max: number, step: number, value: number): void {
    const row = document.createElement("div");
    row.style.cssText = "display:flex;align-items:center;margin-bottom:4px;gap:8px;";

    const lbl = document.createElement("span");
    lbl.textContent = label;
    lbl.style.cssText = `font:11px ${FONT};color:${TEXT_DIM};width:100px;`;

    const slider = document.createElement("input");
    slider.type = "range";
    slider.min = String(min);
    slider.max = String(max);
    slider.step = String(step);
    slider.value = String(value);
    slider.style.cssText = "flex:1;accent-color:" + ACCENT + ";";

    const val = document.createElement("span");
    val.textContent = String(value);
    val.style.cssText = `font:11px ${FONT};color:${TEXT};width:36px;text-align:right;`;
    val.dataset.sliderVal = key;

    slider.addEventListener("input", () => { val.textContent = slider.value; });

    row.append(lbl, slider, val);
    parent.appendChild(row);
    this.sliders.set(key, slider);
  }

  private setSlider(key: string, value: number): void {
    const slider = this.sliders.get(key);
    if (!slider) return;
    slider.value = String(value);
    const val = this.el.querySelector(`[data-slider-val="${key}"]`);
    if (val) val.textContent = String(value);
  }

  private async submit(
    nameInput: HTMLInputElement,
    passwordInput: HTMLInputElement,
    mapSelect: HTMLSelectElement,
    maxPlayersInput: HTMLInputElement,
    presetSelect: HTMLSelectElement,
    btn: HTMLButtonElement,
  ): Promise<void> {
    this.errorEl.textContent = "";
    btn.textContent = "CREATING...";
    btn.style.pointerEvents = "none";

    try {
      const isCustomMap = mapSelect.value === "__custom" && this.customMapJson;
      const presetKey = presetSelect.value;
      const isCustomPreset = presetKey === "__custom";

      // Build gameConfig from preset or custom sliders (mode comes from the map, not host)
      const gameConfig: Record<string, unknown> = {};

      if (isCustomPreset) {
        gameConfig.preset = "custom";
        const controller: Record<string, number> = {};
        for (const key of ["maxSpeed", "airAccel", "groundAccel", "friction", "jumpVelocity"]) {
          const slider = this.sliders.get(key);
          if (slider) controller[key] = parseFloat(slider.value);
        }
        gameConfig.controller = controller;
        const gravSlider = this.sliders.get("gravity");
        if (gravSlider) gameConfig.gravity = parseFloat(gravSlider.value);
      } else {
        gameConfig.preset = presetKey;
      }

      const password = passwordInput.value.trim() || undefined;
      const communityCode = this.mapCodeInput.value.trim();

      let mapSource: ServerConfig["mapSource"];
      if (communityCode) {
        mapSource = { type: "community", code: communityCode };
      } else if (isCustomMap) {
        mapSource = { type: "inline", json: this.customMapJson! };
      } else {
        mapSource = { type: "builtin", name: mapSelect.value };
      }

      const config: ServerConfig = {
        name: nameInput.value.trim() || "My Server",
        maxPlayers: parseInt(maxPlayersInput.value) || 8,
        password,
        mapSource,
        gameConfig,
      };

      const result = await createServer(this.relayHttp, config);
      if (password) result.wsUrl += `?password=${encodeURIComponent(password)}`;
      this.callbacks.onCreated(result);
    } catch (err) {
      this.errorEl.textContent = (err instanceof Error ? err.message : String(err));
      btn.textContent = "HOST";
      btn.style.pointerEvents = "";
    }
  }
}

function inputStyle(): string {
  return (
    `width:100%;box-sizing:border-box;padding:6px 8px;background:${INPUT_BG};` +
    `color:${TEXT};border:1px solid ${BORDER};font:12px ${FONT};outline:none;margin-bottom:2px;`
  );
}
